import { useState, useCallback, useEffect, MutableRefObject } from 'react';
import L from 'leaflet';
import { accessService } from '../../../services/accessService';
import { SpatialLayer, SpatialStats, FeatureProps, GeoJSONFeatureCollection } from '../types';

export interface UseMapLayersResult {
  layers: SpatialLayer[];
  stats: SpatialStats | null;
  activeLayers: Set<string>;
  featureData: Record<string, GeoJSONFeatureCollection>;
  selectedFeature: FeatureProps | null;
  loading: boolean;
  error: string | null;
  toggleLayer: (layerId: string) => void;
  selectFeature: (feature: FeatureProps | null) => void;
  activeFilters: Record<string, string>;
  setActiveFilter: (layerCode: string, filterValue: string) => void;
  clearFilter: (layerCode: string) => void;
  clearAll: () => void;
}

/** Compute a LatLngBounds from a GeoJSON FeatureCollection */
function boundsFromFeatures(fc: GeoJSONFeatureCollection): L.LatLngBounds | null {
  const latlngs: L.LatLng[] = [];
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === 'Point') {
      latlngs.push(L.latLng(g.coordinates[1], g.coordinates[0]));
    } else if (g.type === 'Polygon' && g.coordinates?.[0]) {
      for (const [lng, lat] of g.coordinates[0]) latlngs.push(L.latLng(lat, lng));
    } else if (g.type === 'MultiPolygon' && g.coordinates) {
      for (const poly of g.coordinates) {
        if (poly[0]) for (const [lng, lat] of poly[0]) latlngs.push(L.latLng(lat, lng));
      }
    }
  }
  if (latlngs.length === 0) return null;
  return L.latLngBounds(latlngs);
}

export function useMapLayers(mapInstanceRef?: MutableRefObject<L.Map | null>): UseMapLayersResult {
  const [layers, setLayers] = useState<SpatialLayer[]>([]);
  const [stats, setStats] = useState<SpatialStats | null>(null);
  const [activeLayers, setActiveLayers] = useState<Set<string>>(new Set());
  const [featureData, setFeatureData] = useState<Record<string, GeoJSONFeatureCollection>>({});
  const [selectedFeature, setSelectedFeature] = useState<FeatureProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [layersRes, statsRes] = await Promise.all([
        accessService.get<{ layers: SpatialLayer[] }>('/spatial/layers'),
        accessService.get<SpatialStats>('/spatial/stats'),
      ]);

      const loadedLayers = layersRes.data.layers || [];
      setLayers(loadedLayers);
      setStats(statsRes.data);
    } catch (err: any) {
      console.error('[SPATIAL] Load error:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to load spatial data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fitToData = useCallback((fc: GeoJSONFeatureCollection) => {
    const map = mapInstanceRef?.current;
    if (!map || !fc.features.length) return;
    const bounds = boundsFromFeatures(fc);
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [mapInstanceRef]);

  const toggleLayer = useCallback(
    (layerId: string) => {
      setActiveLayers((prev) => {
        const next = new Set(prev);
        if (next.has(layerId)) {
          next.delete(layerId);
        } else {
          next.add(layerId);
          if (featureData[layerId]) {
            fitToData(featureData[layerId]);
          } else {
            accessService
              .get<GeoJSONFeatureCollection>(`/spatial/layers/${layerId}/features`)
              .then((res) => {
                setFeatureData((fd) => ({ ...fd, [layerId]: res.data }));
                fitToData(res.data);
              })
              .catch(() => {});
          }
        }
        return next;
      });
    },
    [featureData, fitToData]
  );

  const setActiveFilter = useCallback((layerCode: string, filterValue: string) => {
    setActiveFilters((prev) => {
      // Toggle off if same value clicked
      if (prev[layerCode] === filterValue) {
        const next = { ...prev };
        delete next[layerCode];
        return next;
      }
      return { ...prev, [layerCode]: filterValue };
    });
  }, []);

  const clearFilter = useCallback((layerCode: string) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      delete next[layerCode];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setActiveLayers(new Set());
    setActiveFilters({});
    setSelectedFeature(null);
  }, []);

  return {
    layers,
    stats,
    activeLayers,
    featureData,
    selectedFeature,
    loading,
    error,
    toggleLayer,
    selectFeature: setSelectedFeature,
    activeFilters,
    setActiveFilter,
    clearFilter,
    clearAll,
  };
}
